import { Add } from "@mui/icons-material";
import { Box, Divider, Typography, Button } from "@mui/material";
import { useExplorerStore } from "../../../data/ExplorerStore";
import DefaultQueryGroupFilters from "./DefaultQueryGroupFilters";
import DimensionSelect from "./DimensionSelect";
import GroupingSection from "./GroupingSection";

interface Props {
  metricGroupIndex: number;
}

export default function QueryForm({ metricGroupIndex }: Props) {
  const isQueryGroup = metricGroupIndex === 0;
  const metricGroup = useExplorerStore(
    ({ metricGroups }) => metricGroups[metricGroupIndex]
  );
  const isLoading = useExplorerStore(
    ({ filters }) => !filters
  );
  const metrics = useExplorerStore(({ metrics }) => metrics);
  const addDimension = useExplorerStore(({ addDimension }) => addDimension);
  const updateMetricGroup = useExplorerStore(
    ({ updateMetricGroup }) => updateMetricGroup
  );

  // Grouping needs a "who" and something to describe it: two breakdowns at least
  const canGroup =
    isQueryGroup && metricGroup.dimensions.filter((d) => d.name).length >= 2;

  return (
    <>
      <Box mb={1.5}>
        <Typography mb={0.5} fontSize={15} fontWeight={600}>
          Breakdowns
        </Typography>
        {isLoading ? (
          <Typography>Loading filters...</Typography>
        ) : (
          <>
            {metricGroup.dimensions.map((d, i) => (
              <DimensionSelect
                metricGroupIndex={metricGroupIndex}
                index={i}
                key={`queryBreakdown:${i}`}
                dimensionType="dimensions"
              />
            ))}
            <Button
              startIcon={<Add />}
              sx={{
                mt: 1.5,
                textTransform: "none",
                fontSize: "0.8rem",
                color: "rgba(0, 0, 0, 0.6)",
                borderColor: "#bdbdbd",
                "&:hover": { backgroundColor: "#eeeeee" },
              }}
              onClick={() => addDimension(metricGroupIndex, "dimensions")}
              variant="outlined"
              size="small"
            >
              Add Breakdown
            </Button>
            {canGroup && (
              <GroupingSection
                metricGroup={metricGroup}
                metrics={metrics}
                onChange={(next) => updateMetricGroup(metricGroupIndex, next)}
              />
            )}
          </>
        )}
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box mb={1.5}>
        <Typography mb={0.5} fontSize={15} fontWeight={600}>
          Filters
        </Typography>
        {isQueryGroup && (
          <>
            <DefaultQueryGroupFilters />
            <Divider sx={{ my: 1.5 }} />
          </>
        )}
        {isLoading ? (
          <Typography>Loading filters...</Typography>
        ) : (
          <>
            {metricGroup.filters.map(
              (filter, i) =>
                (!isQueryGroup || i > 2) && (
                  <DimensionSelect
                    metricGroupIndex={metricGroupIndex}
                    index={i}
                    key={`queryFilter:${i}`}
                    dimensionType="filters"
                  />
                )
            )}
            <Box>
              <Button
                startIcon={<Add />}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  fontSize: "0.8rem",
                  color: "rgba(0, 0, 0, 0.6)",
                  borderColor: "#bdbdbd",
                  "&:hover": { backgroundColor: "#eeeeee" },
                }}
                onClick={() => addDimension(metricGroupIndex, "filters")}
                variant="outlined"
                size="small"
              >
                Add Filter
              </Button>
            </Box>
          </>
        )}
      </Box>
    </>
  );
}
